import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import adminApiService from '../../services/adminApiService';
import { toast } from 'react-toastify';

const AdminApiManager = () => {
  const [loading, setLoading] = useState(true);
  const [apiData, setApiData] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingApi, setEditingApi] = useState(null);
  const [formData, setFormData] = useState({
    apiName: '',
    apiUrl: '',
    status: false
  });

  useEffect(() => {
    fetchApiData();
  }, []);

  const fetchApiData = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getApiManager();
      if (response.success) {
        setApiData(response.data);
      } else {
        toast.error(response.message || 'Failed to fetch API data');
      }
    } catch (error) {
      console.error('Error fetching API data:', error);
      toast.error('Failed to fetch API data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddApi = async (e) => {
    e.preventDefault();
    try {
      const response = await adminApiService.addApi(formData);
      if (response.success) {
        toast.success('API added successfully');
        setShowAddModal(false);
        setFormData({ apiName: '', apiUrl: '', status: false });
        fetchApiData();
      } else {
        toast.error(response.message || 'Failed to add API');
      }
    } catch (error) {
      console.error('Error adding API:', error);
      toast.error('Failed to add API');
    }
  };

  const handleToggleStatus = async (apiName, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const response = await adminApiService.toggleApiStatus(apiName, newStatus);
      if (response.success) {
        toast.success('API status updated successfully');
        fetchApiData();
      } else {
        toast.error(response.message || 'Failed to update API status');
      }
    } catch (error) {
      console.error('Error updating API status:', error);
      toast.error('Failed to update API status');
    }
  };

  const handleEditApi = async (e) => {
    e.preventDefault();
    try {
      const editData = {
        originalApiName: editingApi.apiName,
        apiName: formData.apiName,
        apiUrl: formData.apiUrl,
        status: formData.status
      };
      const response = await adminApiService.editApi(editData);
      if (response.success) {
        toast.success('API updated successfully');
        setShowEditModal(false);
        setEditingApi(null);
        setFormData({ apiName: '', apiUrl: '', status: false });
        fetchApiData();
      } else {
        toast.error(response.message || 'Failed to update API');
      }
    } catch (error) {
      console.error('Error updating API:', error);
      toast.error('Failed to update API');
    }
  };

  const openEditModal = (api) => {
    setEditingApi(api);
    setFormData({
      apiName: api.apiName,
      apiUrl: api.apiUrl,
      status: api.status
    });
    setShowEditModal(true);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingApi(null);
    setFormData({ apiName: '', apiUrl: '', status: false });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">API Control Panel</h1>
            <p className="text-gray-600 mt-2">Manage API endpoints and configurations</p>
          </div>
          <Link 
            to="/admin/dashboard/settings" 
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            ← Back to Settings
          </Link>
        </div>
      </div>

      {/* Add API Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
        >
          Add API
        </button>
      </div>

      {/* API Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">API Configurations</h2>
        </div>
        
        {apiData?.apiInfo && apiData.apiInfo.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Base URL</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {apiData.apiInfo.map((api, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {api.apiName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {api.apiUrl}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={api.status}
                          onChange={() => handleToggleStatus(api.apiName, api.status)}
                          className="sr-only"
                        />
                        <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          api.status ? 'bg-green-600' : 'bg-gray-200'
                        }`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            api.status ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </div>
                        <span className={`ml-2 text-sm font-medium ${
                          api.status ? 'text-green-600' : 'text-gray-400'
                        }`}>
                          {api.status ? 'Active' : 'Inactive'}
                        </span>
                      </label>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openEditModal(api)}
                        className="text-orange-600 hover:text-orange-900 font-medium"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-8 text-center">
            <div className="text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No APIs configured</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by adding your first API configuration.</p>
            </div>
          </div>
        )}
      </div>

      {/* Add API Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New API</h3>
              <form onSubmit={handleAddApi}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Name
                  </label>
                  <input
                    type="text"
                    value={formData.apiName}
                    onChange={(e) => setFormData({ ...formData, apiName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter API Name"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Base URL
                  </label>
                  <input
                    type="url"
                    value={formData.apiUrl}
                    onChange={(e) => setFormData({ ...formData, apiUrl: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter API Base URL"
                    required
                  />
                </div>
                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Enable API</span>
                  </label>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Add API
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit API Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit API</h3>
              <form onSubmit={handleEditApi}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Name
                  </label>
                  <input
                    type="text"
                    value={formData.apiName}
                    onChange={(e) => setFormData({ ...formData, apiName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter API Name"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Base URL
                  </label>
                  <input
                    type="url"
                    value={formData.apiUrl}
                    onChange={(e) => setFormData({ ...formData, apiUrl: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter API Base URL"
                    required
                  />
                </div>
                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">Enable API</span>
                  </label>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminApiManager;